// src/hooks/useInlineAutosave.js
//
// Per-field debounced autosave hook. Lazy create + chained patches.
//
// Sözleşme: caller create/update mutation FONKSİYONLARINI (callable) verir.
// Hook RTK Query'ye bağımlı değildir — test edilebilir ve framework-bağımsız.
//
// Lifecycle:
// 1. setField(name, value) → fieldValues anında güncel.
// 2. O alanın debounce timer'ı reset. Tüm alanlar TEK ortak timer paylaşır
//    (basit ve "aynı debounce penceresinde iki alan dolarsa tek payload" davranışı).
// 3. Timer dolunca:
//    - record._id yoksa: shouldCreate(form) çalıştır. False → bekle. True →
//      createMutation(buildPayload(form)) çağır. Dönen _id state'e işle,
//      onCreated tetikle.
//    - record._id varsa: updateMutation({ ...buildPayload(form), anamnezId: _id })
//      çağır.
// 4. createTriggerField verildiyse, o alanın setField'i createDebounceMs ile
//    diğer alanlardan ayrı bir timer çalıştırır (lazy create için daha hızlı).
// 5. registerAutoSave varsa her mutation promise'ini ona ilet (muayene flushAll
//    entegrasyonu).

import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_DEBOUNCE_MS = 500;

export default function useInlineAutosave({
  record,
  createMutation,
  updateMutation,
  shouldCreate,
  canSave,
  buildPayload,
  recordIdField = "anamnezId",
  fieldsFromRecord,
  onCreated,
  onSaved,
  onError,
  registerAutoSave,
  registerFlush,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  createTriggerField = null,
  createDebounceMs = 200,
  manualCreate = false,
  // Mevcut kayıt güncellemesi başarısız olursa (ör. 402 token yetersiz, 423),
  // formu sunucudaki son hale geri al — başarısız değişiklik UI'da kalmasın.
  revertOnError = false,
}) {
  const toForm = typeof fieldsFromRecord === "function"
    ? fieldsFromRecord
    : defaultRecordToForm;

  // record değiştiğinde (örn. listede başka kayıt seçildi) form sıfırlanır.
  const [fieldValues, setFieldValues] = useState(() => toForm(record));
  const [saveStatus, setSaveStatus] = useState("idle");
  const recordIdRef = useRef(record?._id || null);
  const recordRef = useRef(record);
  recordRef.current = record;
  const fieldValuesRef = useRef(fieldValues);
  fieldValuesRef.current = fieldValues;

  const mainTimerRef = useRef(null);
  const createTimerRef = useRef(null);
  const inFlightRef = useRef(null); // Promise — yeni save'i bekletmek için chain

  // record prop'u değişirse state'i yeni kayıtla yenile.
  useEffect(() => {
    setFieldValues(toForm(record));
    recordIdRef.current = record?._id || null;
    setSaveStatus("idle");
    if (mainTimerRef.current) clearTimeout(mainTimerRef.current);
    if (createTimerRef.current) clearTimeout(createTimerRef.current);
    mainTimerRef.current = null;
    createTimerRef.current = null;
  }, [record?._id]);

  const runSave = useCallback(async () => {
    const form = fieldValuesRef.current;
    const id = recordIdRef.current;

    // Zorunlu alan guard'ı: canSave(form) false ise (örn. tedavide tanı yok)
    // hem create hem update atlanır — geçersiz state sunucuya yazılmaz.
    if (typeof canSave === "function" && !canSave(form)) {
      setSaveStatus("idle");
      return;
    }

    // Önceki save bitmediyse bekle (chain).
    if (inFlightRef.current) {
      try {
        await inFlightRef.current;
      } catch (_) {
        /* ignore — kendisi onError'a düştü */
      }
    }

    setSaveStatus("saving");

    let promise;
    try {
      if (!id) {
        if (!shouldCreate(form)) {
          setSaveStatus("idle");
          return;
        }
        promise = Promise.resolve(createMutation(buildPayload(form)));
        if (registerAutoSave) promise = registerAutoSave(promise);
        inFlightRef.current = promise;
        const res = await promise;
        const created = res?.data || res || null;
        if (created?._id) {
          recordIdRef.current = created._id;
          if (typeof onCreated === "function") onCreated(created);
        }
      } else {
        const payload = { ...buildPayload(form), [recordIdField]: id };
        promise = Promise.resolve(updateMutation(payload));
        if (registerAutoSave) promise = registerAutoSave(promise);
        inFlightRef.current = promise;
        const res = await promise;
        if (typeof onSaved === "function") onSaved(res?.data || res || null);
      }
      setSaveStatus("saved");
    } catch (err) {
      setSaveStatus("error");
      if (typeof onError === "function") onError(err);
      // Güncelleme başarısızsa formu sunucudaki son hale geri al — başarısız
      // değişiklik (ör. token yetersiz) UI'da uygulanmış gibi görünmesin.
      // (Yeni kayıt/create hatasında geri alma yok; kullanıcı girdisi korunur.)
      if (revertOnError && recordIdRef.current) {
        setFieldValues(toForm(recordRef.current));
      }
    } finally {
      inFlightRef.current = null;
    }
  }, [
    createMutation,
    updateMutation,
    shouldCreate,
    canSave,
    buildPayload,
    recordIdField,
    onCreated,
    onSaved,
    onError,
    registerAutoSave,
    revertOnError,
    toForm,
  ]);

  // Flush hook (Muayene Tamamla butonu için).
  useEffect(() => {
    if (!registerFlush) return undefined;
    const unregister = registerFlush(async () => {
      if (mainTimerRef.current) {
        clearTimeout(mainTimerRef.current);
        mainTimerRef.current = null;
      }
      if (createTimerRef.current) {
        clearTimeout(createTimerRef.current);
        createTimerRef.current = null;
      }
      await runSave();
    });
    return unregister;
  }, [registerFlush, runSave]);

  // Unmount cleanup.
  useEffect(
    () => () => {
      if (mainTimerRef.current) clearTimeout(mainTimerRef.current);
      if (createTimerRef.current) clearTimeout(createTimerRef.current);
    },
    []
  );

  const setField = useCallback(
    (name, value) => {
      setFieldValues((prev) => ({ ...prev, [name]: value }));

      // manualCreate modunda autosave hiç çalışmaz (hem yeni hem mevcut kayıt
      // için) — saveNow() çağrılana kadar form sadece UI state olarak tutulur.
      if (manualCreate) return;

      // Eğer kayıt henüz yoksa VE bu alan createTriggerField ise, ayrı (daha
      // kısa) timer ile lazy create'i tetikle.
      const isTrigger =
        !recordIdRef.current &&
        createTriggerField &&
        name === createTriggerField;

      if (isTrigger) {
        if (createTimerRef.current) clearTimeout(createTimerRef.current);
        createTimerRef.current = setTimeout(() => {
          createTimerRef.current = null;
          runSave();
        }, createDebounceMs);
      } else {
        if (mainTimerRef.current) clearTimeout(mainTimerRef.current);
        mainTimerRef.current = setTimeout(() => {
          mainTimerRef.current = null;
          runSave();
        }, debounceMs);
      }
    },
    [manualCreate, createTriggerField, createDebounceMs, debounceMs, runSave]
  );

  // Explicit save trigger (manualCreate modu için "Kaydet" butonu çağırır).
  const saveNow = useCallback(() => {
    if (mainTimerRef.current) {
      clearTimeout(mainTimerRef.current);
      mainTimerRef.current = null;
    }
    if (createTimerRef.current) {
      clearTimeout(createTimerRef.current);
      createTimerRef.current = null;
    }
    return runSave();
  }, [runSave]);

  const resetForm = useCallback(() => {
    setFieldValues(toForm(record));
    setSaveStatus("idle");
  }, [record, toForm]);

  return {
    fieldValues,
    setField,
    saveStatus,
    resetForm,
    saveNow,
  };
}

// Default fallback — caller'lar `fieldsFromRecord` vermediyse `record`'u
// olduğu gibi (varsa) veya boş obje döndürür. Anamnez/Tetkik gibi domain'ler
// kendi `fieldsFromRecord` factory'lerini geçer.
function defaultRecordToForm(record) {
  if (!record) return {};
  return { ...record };
}
